import React, { useState } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui';
import { LocationFilter, LocationFilterValue } from '../LocationFilter';
import { PROGRAM_OPTIONS, ASSET_TYPE_OPTIONS } from '../../constants';
import { BrandfolderAsset } from '../../types/brandfolder';
import { getResizedImageUrl } from '../../utils/imageUrl';

export interface DamAssetUpload {
  id: string;
  title: string;
  description?: string;
  source: 'Brandfolder';
  type: 'Image' | 'Video';
  asset_type: string;
  village_id: string;
  program: string;
  date?: string;
}

interface DamAssetEditorModalProps {
  assets: BrandfolderAsset[];
  onSave: (editedAssets: DamAssetUpload[]) => void;
  onClose: () => void;
}

interface EditableAsset {
  bf_asset_id: string;
  url: string;
  title: string;
  description: string;
  type: 'Image' | 'Video';
  program: string;
  date: string;
  location: LocationFilterValue;
  errors: string[];
}

export const DamAssetEditorModal: React.FC<DamAssetEditorModalProps> = ({
  assets,
  onSave,
  onClose
}) => {
  const [editableAssets, setEditableAssets] = useState<EditableAsset[]>(() =>
    assets.map(asset => ({
      bf_asset_id: asset.bf_asset_id,
      url: asset.url,
      title: '',
      description: '',
      type: 'Image' as const,
      program: '',
      date: '',
      location: {},
      errors: []
    }))
  );

  const validateAsset = (asset: EditableAsset): string[] => {
    const errors: string[] = [];
    if (!asset.title.trim()) errors.push('Title is required');
    if (asset.title.length > 255) errors.push('Title must be 255 characters or less');
    if (!asset.program) errors.push('Program is required');
    if (!asset.location.village_id) errors.push('Village is required');
    return errors;
  };

  const updateField = (index: number, field: keyof EditableAsset, value: any) => {
    setEditableAssets(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      updated[index].errors = validateAsset(updated[index]);
      return updated;
    });
  };

  const updateLocation = (index: number, location: LocationFilterValue) => {
    setEditableAssets(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], location };
      updated[index].errors = validateAsset(updated[index]);
      return updated;
    });
  };

  const allValid = editableAssets.every(asset => {
    const errors = validateAsset(asset);
    return errors.length === 0;
  });

  const handleSave = () => {
    // Re-validate all
    const validated = editableAssets.map(asset => ({
      ...asset,
      errors: validateAsset(asset)
    }));
    setEditableAssets(validated);

    if (validated.some(a => a.errors.length > 0)) return;

    const uploads: DamAssetUpload[] = validated.map(asset => ({
      id: asset.bf_asset_id,
      title: asset.title.trim(),
      description: asset.description.trim() || undefined,
      source: 'Brandfolder' as const,
      type: asset.type,
      asset_type: 'Brandfolder Image',
      village_id: asset.location.village_id!,
      program: asset.program,
      date: asset.date || undefined
    }));

    onSave(uploads);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">DAM Asset Metadata</h2>
            <p className="text-sm text-gray-600 mt-1">
              Add metadata for {editableAssets.length} Brandfolder asset{editableAssets.length !== 1 ? 's' : ''} before uploading
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          <div className="space-y-6">
            {editableAssets.map((asset, index) => (
              <div key={asset.bf_asset_id} className="border border-gray-200 rounded-lg p-4">
                {/* Asset Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                      <img
                        src={getResizedImageUrl(asset.url, 100)}
                        alt={asset.title || 'Brandfolder asset'}
                        referrerPolicy="no-referrer"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900">Asset {index + 1}</h3>
                      <p className="text-sm text-gray-500 truncate">ID: {asset.bf_asset_id}</p>
                    </div>
                  </div>

                  {asset.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-2">
                      <div className="flex items-center">
                        <AlertCircle className="h-4 w-4 text-red-500 mr-1" />
                        <span className="text-xs text-red-700">
                          {asset.errors.length} error{asset.errors.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <ul className="text-xs text-red-600 mt-1 space-y-1">
                        {asset.errors.map((error, i) => (
                          <li key={i}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Title */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={asset.title}
                      onChange={(e) => updateField(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter asset title"
                      maxLength={255}
                    />
                    <p className="text-xs text-gray-500 mt-1">{asset.title.length}/255</p>
                  </div>

                  {/* Description */}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={asset.description}
                      onChange={(e) => updateField(index, 'description', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter description (optional)"
                      maxLength={1000}
                    />
                  </div>

                  {/* Program */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Program <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={asset.program}
                      onChange={(e) => updateField(index, 'program', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Program</option>
                      {PROGRAM_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <select
                      value={asset.type}
                      onChange={(e) => updateField(index, 'type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {ASSET_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={asset.date}
                      onChange={(e) => updateField(index, 'date', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Source (read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Source
                    </label>
                    <input
                      type="text"
                      value="Brandfolder"
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                    />
                  </div>

                  {/* Asset Type (read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Asset Type
                    </label>
                    <input
                      type="text"
                      value="Brandfolder Image"
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                    />
                  </div>

                  {/* Location */}
                  <div className="col-span-2 border-t pt-4 mt-2">
                    <LocationFilter
                      value={asset.location}
                      onChange={(loc) => updateLocation(index, loc)}
                      showVillages={true}
                    />
                    {!asset.location.village_id && asset.errors.includes('Village is required') && (
                      <p className="text-xs text-red-600 mt-1">Please select a village</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div>
            {!allValid && (
              <div className="flex items-center text-red-600">
                <AlertCircle className="h-4 w-4 mr-2" />
                <span className="text-sm">Please fix validation errors before saving</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!allValid}
            >
              <Save className="h-4 w-4 mr-2" />
              Save & Upload ({editableAssets.length})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
